import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Container from 'react-bootstrap/Container';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Stack from 'react-bootstrap/Stack';
import axios from 'axios';
import { toast } from 'react-toastify';
import Global from '../../../components/Meeting/Details/Cards/Global/Global';
import Documents from '../../../components/Meeting/Details/Cards/Documents/Documents';
import Members from '../../../components/Meeting/Details/Cards/Members/Members';
import Main from '../../../layouts/Main/Main';
import Loading from '../../../components/Loading/Loading';

import { IMeeting } from '../../../types';
import Agenda from '../../../components/Meeting/Details/Cards/Agenda/Agenda';
import { IBreadcrumbProps } from '../../../components/Header/Header';
import Settings from '../../../components/Meeting/Details/Cards/Settings/Settings';
import Guests from '../../../components/Meeting/Details/Cards/Guests/Guests';
import Stats from '../../../components/Meeting/Details/Cards/Stats/Stats';
import Button from 'react-bootstrap/Button';
import { FaClone, FaRegTrashCan } from 'react-icons/fa6';
import { IconWrapper } from '../../../components/IconWrapper/IconWrapper';
import { FaCalendarDays, FaLocationDot } from 'react-icons/fa6';
import styles from './Details.module.scss';
import moment from 'moment';
import 'moment/locale/fr';

const Details = () => {
  const params = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [meeting, setMeeting] = useState<IMeeting | undefined>(undefined);
  const [breadcrumb, setBreadcrumb] = useState<IBreadcrumbProps[]>([]);

  useEffect(() => {
    if (meeting) {
      setBreadcrumb([
        { name: 'Home', path: '/' },
        { name: 'Meetings', path: '/meeting' },
        { name: meeting.name, path: `/meeting/${params.id}`, active: true },
      ]);
    }
  }, [meeting, params.id]);

  const getMeetingDetails = () => {
    axios
      .get(`/api/meeting/${params.id}`)
      .then((response) => {
        if (response.status === 200) {
          setMeeting(response.data);
        }
      })
      .catch((err) => {
        toast.error(err.response.data);
      })
      .finally(() => {
        setTimeout(() => setLoading(false), 250);
      });
  };

  const handleOnChange = () => {
    getMeetingDetails();
  };

  const handleClone = () => {
    axios
      .post(`/api/meeting/${params.id}/clone`)
      .then((response) => navigate(`/meeting/${response.data._id}`))
      .catch((error) => toast.error(error.response?.data ?? 'Unable to clone meeting'));
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this meeting?')) return;
    axios
      .delete(`/api/meeting/${params.id}`)
      .then(() => navigate('/meeting'))
      .catch((error) => toast.error(error.response?.data ?? 'Unable to delete meeting'));
  };

  useEffect(() => {
    setLoading(true);
    getMeetingDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Main
      breadcrumb={breadcrumb}
      actions={
        <>
          <Button variant="primary" size="sm" onClick={handleClone}>
            <IconWrapper icon={FaClone} /> Clone meeting
          </Button>{' '}
          <Button variant="outline-danger" size="sm" onClick={handleDelete}>
            <IconWrapper icon={FaRegTrashCan} /> Delete meeting
          </Button>
        </>
      }
    >
      <Container className="mt-3">
        <Loading isLoading={loading}>
          <>
            {!meeting && <Alert variant="danger">Meeting not found</Alert>}
            {meeting && (
              <div className={styles.Page}>
                <section className={styles.Hero}>
                  <Row className="align-items-center">
                    <Col md={8} className={styles.HeroContent}>
                      <div className={styles.Eyebrow}>Meeting · {meeting.parameters.global.antenna}</div>
                      <h1 className={styles.Title}>{meeting.name}</h1>
                      <div className={styles.Meta}>
                        <span className={styles.MetaItem}><IconWrapper icon={FaCalendarDays} />{moment(meeting.event_date).format('LL · HH:mm')}</span>
                        <span className={styles.MetaItem}><IconWrapper icon={FaLocationDot} />{meeting.city}{meeting.site ? ` · ${meeting.site}` : ''}</span>
                      </div>
                    </Col>
                    <Col md={4} className={styles.Metrics}>
                      <div className={styles.Metric}><strong>{meeting.members.length}</strong><span>Members</span></div>
                      <div className={styles.Metric}><strong>{meeting.guests.length}</strong><span>Guests</span></div>
                    </Col>
                  </Row>
                </section>
                <Stack gap={4}>
                <Row className={styles.SectionRow}>
                  <Col sm={4}>
                    <Stack gap={4}>
                      <Global meeting={meeting} onChange={handleOnChange} />
                      <Settings meeting={meeting} onChange={handleOnChange} />
                    </Stack>
                  </Col>
                  <Col sm={4}>
                    <Agenda meeting={meeting} onChange={handleOnChange} />
                  </Col>
                  <Col sm={4}>
                    <Stack gap={4}>
                      <Stats meeting={meeting} />
                      <Documents meeting={meeting} />
                    </Stack>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <Members meeting={meeting} onChange={handleOnChange} />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <Guests meeting={meeting} onChange={handleOnChange} />
                  </Col>
                </Row>
                </Stack>
              </div>
            )}
          </>
        </Loading>
      </Container>
    </Main>
  );
};

export default Details;
